import {HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AuthResponse, ReferenceBookResponse, UserAttributesApiService, UserAttributesResponse} from '@npck/af-frontend-api';
import {isTokenExpired, parseJwt, RouterService} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {LocalStorageService, SessionStorageService} from 'ngx-webstorage';
import {BehaviorSubject} from 'rxjs';
import {filter} from 'rxjs/operators';
import {Path} from '../path';
import {RETURN_URL, saveReturnUrl, TOKEN_RESPONSE} from './browser-storage.keys';

@Injectable({
    providedIn: 'root',
})
export class AuthHolderService {
    authResponse: AuthResponse;
    isAuthorized = new BehaviorSubject<boolean>(false);
    currentOrgInfo: ReferenceBookResponse;
    private userAttributes?: UserAttributesResponse;

    constructor(
        private localStorageService: LocalStorageService,
        private sessionStorageService: SessionStorageService,
        private routerService: RouterService,
        private dialogService: DialogService,
    ) {
        this.trackNavigation();
        this.syncLogoutAcrossTabs();
        this.authResponse = JSON.parse(this.localStorageService.retrieve(TOKEN_RESPONSE));
        const token = this.authResponse?.access_token;
        if (token && isTokenExpired(token)) {
            this.logout();
            return;
        }
        this.isAuthorized.next(!!token);
    }

    get fullName(): string {
        const parsedJwt = this.authResponse && parseJwt(this.authResponse.access_token);
        return parsedJwt && `${parsedJwt.firstname} ${parsedJwt.lastname}`;
    }

    get username(): string {
        const parsedJwt = this.authResponse && parseJwt(this.authResponse.access_token);
        return parsedJwt && parsedJwt.username;
    }

    get organizationBin(): string {
        return this.userAttributes?.bin;
    }

    get organizationType(): string {
        return this.userAttributes?.organizationType;
    }

    get isOrganizationScopedAdmin(): boolean {
        return this.hasAccess(['organization-supervisor']) && !this.hasAccess(['npck-admin']);
    }

    get phoneNumber(): string {
        return this.userAttributes?.phoneNumber;
    }

    get passwordExpiryDaysLeft(): number {
        return this.userAttributes?.passwordExpiryDaysLeft;
    }

    get userId(): string | null {
        const token = this.authResponse?.access_token;
        if (!token) {
            return null;
        }
        const parsedJwt = parseJwt(token);
        return parsedJwt?.sub ?? null;
    }

    private get clientRoles(): Role[] {
        const roles = this.authResponse?.roles;
        if (roles) {
            return roles as Role[];
        }
        // TODO remove after release DEVAF-3731
        const token = this.authResponse?.access_token;
        if (!token) {
            return [];
        }
        const parsedJwt = parseJwt(token);
        return parsedJwt?.resource_access?.[parsedJwt.azp]?.roles ?? [];
    }

    setUserAttributes(attrs: UserAttributesResponse): void {
        this.userAttributes = attrs;
    }

    hasAccess(roles: Role[]): boolean {
        if (!roles.length) {
            return true;
        }
        return this.clientRoles.some((role: Role) => roles.includes(role));
    }

    storeAuthToken(tokenResponse: AuthResponse): void {
        this.authResponse = tokenResponse;
        this.localStorageService.store(TOKEN_RESPONSE, JSON.stringify(tokenResponse));
        this.isAuthorized.next(true);
    }

    isTokenExpired(): boolean {
        if (!this.authResponse) {
            return true;
        }
        return !!this.authResponse && isTokenExpired(this.authResponse.access_token) && this.isRefreshTokenExpired();
    }

    isRefreshTokenExpired(): boolean {
        if (!this.authResponse) {
            return true;
        }

        return !!this.authResponse && isTokenExpired(this.authResponse?.refresh_token);
    }

    logout(reason?: string): void {
        this.isAuthorized.next(false);
        this.clearToken();
        this.userAttributes = null;
        if (!reason) {
            this.sessionStorageService.clear(RETURN_URL);
        }
        this.dialogService.closeAllDialogs();
        this.routerService.navigate(Path.ROOT, {replaceUrl: true});
    }

    setCurrentOrganization(referenceBookResponse: ReferenceBookResponse): void {
        this.currentOrgInfo = referenceBookResponse;
    }

    loadUserAttributes(userAttributesApi: UserAttributesApiService): void {
        if (!this.isAuthorized.value || !this.authResponse?.access_token) {
            return;
        }
        userAttributesApi.getUserAttributes().subscribe({
            next: (attrs: UserAttributesResponse) => this.setUserAttributes(attrs),
            error: (err: HttpErrorResponse) => {
                console.error('[AuthHolderService] Failed to load user attributes', err);
            },
        });
    }

    private syncLogoutAcrossTabs(): void {
        this.localStorageService.observe(TOKEN_RESPONSE).subscribe(value => {
            if (!value && this.isAuthorized.value) {
                this.isAuthorized.next(false);
                this.authResponse = null;
                this.userAttributes = null;
                this.dialogService.closeAllDialogs();
                this.routerService.navigate(Path.ROOT, {replaceUrl: true});
            }
        });
    }

    private trackNavigation(): void {
        this.routerService.navigationEnd().pipe(
            filter(event => {
                const url = event.urlAfterRedirects;
                return url !== '/'
                    && !url.startsWith('/' + Path.LOGIN)
                    && !url.startsWith('/' + Path.RESET_PASSWORD);
            }),
        ).subscribe(event => saveReturnUrl(this.sessionStorageService, event.urlAfterRedirects));
    }

    private clearToken(): void {
        this.authResponse = null;
        this.localStorageService.clear();
    }
}

export type Role =
    'npck-admin' |
    'npck-user' |
    'government-user' |
    'finance-user' |
    'mobile-operator-user' |
    'organization-supervisor' |
    'potential-victim-manager' |
    'victim-viewer';
